import type { ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import { tokens } from '@/styles/tokens';
import { DocumentationHintList } from './DocumentationHintList';

/**
 * The empty-legend state (Figma 36557-158083): a large outlined glyph, a heading,
 * and an optional list of how-it-works hints.
 *
 * Shared rather than page-local because both documentation surfaces show the same
 * thing when nothing has been marked yet — and because an empty legend is the
 * first view every auditor sees, so it is the worst place for the two pages to
 * drift apart.
 *
 * The hints are optional: a surface that has nothing useful to teach passes the
 * heading alone rather than inventing filler. So is the note, a sentence that qualifies
 * the heading when the list could not be read at all, so the same state never claims
 * there is nothing.
 */

const Root = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: 8,
  // 32 here plus the list's own 16 lands the 48px the design puts above and below.
  padding: '32px 8px',
});

const Glyph = styled('div')({
  display: 'flex',
  color: tokens.dataAnalysis.textSoft,
  '& svg': { display: 'block' },
});

const Heading = styled('div')({
  fontSize: 14,
  fontWeight: 700,
  lineHeight: 1.6,
  color: tokens.dataAnalysis.textBody,
});

/** The note under the heading, in the hint lines' type; no bullet, since it is no how-to. */
const Note = styled('p')({
  margin: 0,
  maxWidth: 280,
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 1.4,
  color: tokens.dataAnalysis.textMuted,
});

export interface DocumentationLegendEmptyProps {
  /** The outlined glyph above the heading (typically 32px). */
  icon?: ReactNode;
  heading: ReactNode;
  /** Optional sentence under the heading, for a list that could not be read. */
  note?: ReactNode;
  /** Optional how-it-works lines. Omit when the surface has nothing to teach. */
  hints?: ReactNode[];
  'data-testid'?: string;
}

export function DocumentationLegendEmpty({
  icon,
  heading,
  note,
  hints,
  'data-testid': dataTestId = 'legend-empty',
}: DocumentationLegendEmptyProps) {
  return (
    <Root data-testid={dataTestId}>
      {icon && <Glyph aria-hidden>{icon}</Glyph>}
      <Heading>{heading}</Heading>
      {note && <Note data-testid={`${dataTestId}-note`}>{note}</Note>}
      {hints && hints.length > 0 && <DocumentationHintList hints={hints} />}
    </Root>
  );
}
